import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { notificationService, type Notification as NotificationItem } from '@/services/notificationService'

export interface Notification {
  show: boolean
  message: string
  color: string
  timeout: number
}

export const useNotificationStore = defineStore('notification', () => {
  // Snackbar notification (for toast messages)
  const current = ref<Notification>({
    show: false,
    message: '',
    color: 'info',
    timeout: 3000
  })

  // Notification list (for notification panel)
  const notifications = ref<NotificationItem[]>([])
  const unreadCount = ref(0)
  const loading = ref(false)

  const hasUnread = computed(() => unreadCount.value > 0)

  function showNotification(message: string, color: string = 'info', timeout: number = 3000) {
    current.value = {
      show: true,
      message,
      color,
      timeout
    }
  }

  function showSuccess(message: string) {
    showNotification(message, 'success')
  }

  function showError(message: string) {
    showNotification(message, 'error', 5000)
  }

  function showWarning(message: string) {
    showNotification(message, 'warning')
  }

  function showInfo(message: string) {
    showNotification(message, 'info')
  }

  function hide() {
    current.value.show = false
  }

  // Notification list methods
  async function fetchNotifications(unreadOnly: boolean = false) {
    loading.value = true
    try {
      notifications.value = await notificationService.getNotifications(unreadOnly)
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
    } finally {
      loading.value = false
    }
  }

  async function fetchUnreadCount() {
    try {
      unreadCount.value = await notificationService.getUnreadCount()
    } catch (error) {
      console.error('Failed to fetch unread count:', error)
    }
  }

  async function markAsRead(notificationId: string) {
    try {
      await notificationService.markAsRead(notificationId)
      
      // Update local state
      const notification = notifications.value.find(n => n.id === notificationId)
      if (notification && !notification.is_read) {
        notification.is_read = true
        unreadCount.value = Math.max(0, unreadCount.value - 1)
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
      throw error
    }
  }

  async function markAllAsRead() {
    try {
      await notificationService.markAllAsRead()
      
      // Update local state
      notifications.value.forEach(n => {
        n.is_read = true
      })
      unreadCount.value = 0
    } catch (error) {
      console.error('Failed to mark all as read:', error)
      throw error
    }
  }

  async function deleteNotification(notificationId: string) {
    try {
      await notificationService.deleteNotification(notificationId)
      
      // Update local state
      const index = notifications.value.findIndex(n => n.id === notificationId)
      if (index !== -1) {
        const notification = notifications.value[index]
        if (!notification.is_read) {
          unreadCount.value = Math.max(0, unreadCount.value - 1)
        }
        notifications.value.splice(index, 1)
      }
    } catch (error) {
      console.error('Failed to delete notification:', error)
      throw error
    }
  }

  async function refresh() {
    await Promise.all([
      fetchNotifications(),
      fetchUnreadCount()
    ])
  }

  return {
    // Snackbar
    current,
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    hide,
    // Notification list
    notifications,
    unreadCount,
    hasUnread,
    loading,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh
  }
})